/* eslint-disable complexity */
import {
  fetch_bank_statement_analysis,
  fetch_manual_verification_question_mappings,
  fetch_manual_verification_run,
  open_manual_verification_run,
  refresh_bank_statement_analysis,
  record_manual_verification_decision,
  record_manual_verification_field_result,
  start_bank_statement_analysis
} from '@/services/onboarding_journey_service';
import { manual_run_is_terminal } from './manage_verification_manual_helpers';
import {
  get_item_manual_form_link_id,
  get_run_manual_form_link_id
} from './manage_verification_manual_runtime_helpers';

function get_manual_review_context(item = {}) {
  return {
    entity_profile_id: Number(item?.case_item?.entity_profile_id || item?.case_item?.profile?.id || 0),
    application_id: Number(item?.case_item?.application_id || item?.case_item?.id || 0),
    verification_type: item?.verification_type || '',
    binding_id: Number(item?.manual_binding?.id || item?.binding || item?.binding_id || 0) || null,
    manual_form_link_id: get_item_manual_form_link_id(item)
  };
}

function build_open_manual_run_payload(context) {
  return {
    verification_type: context.verification_type,
    entity_profile_id: context.entity_profile_id,
    application_id: context.application_id || null,
    binding_id: context.binding_id,
    manual_form_link_id: context.manual_form_link_id,
    triggered_by_kind: 'manual_request'
  };
}

function context_missing(context) {
  return !context.entity_profile_id || !context.verification_type;
}

function get_run_field_runs(run, item, get_manual_field_runs) {
  if (Array.isArray(run?.field_runs)) {
    return run.field_runs;
  }

  return get_manual_field_runs(item);
}

function build_modal_item(item, run, get_manual_field_runs) {
  return {
    ...item,
    field_runs: get_run_field_runs(run, item, get_manual_field_runs)
  };
}

function build_manual_review_modal_state(item, run, form_sections, get_manual_field_runs) {
  return {
    visible: true,
    is_submitting: false,
    is_saving_field: false,
    item: build_modal_item(item, run, get_manual_field_runs),
    run,
    form_sections,
    field_form: null,
    decision_status: 'pass',
    decision_reason: run?.decision_reason || ''
  };
}

function build_empty_manual_review_modal() {
  return {
    visible: false,
    is_submitting: false,
    is_saving_field: false,
    item: null,
    run: null,
    form_sections: [],
    field_form: null,
    decision_status: 'pass',
    decision_reason: ''
  };
}

function build_field_result_payload(run_id, field_form) {
  return {
    run_id,
    section_question_id: Number(field_form?.section_question_id || 0),
    user_answer: field_form.user_answer || '',
    verified_value: field_form.verified_value || '',
    match_status: field_form.match_status || 'unable_to_verify',
    reviewer_comment: field_form.reviewer_comment || '',
    source_evidence: field_form.source_evidence || ''
  };
}

function get_error_message(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function sleep(ms = 0) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function get_run_id(item = {}) {
  return Number(item?.run_id || 0);
}

function get_bank_statement_poll_delay(status = '') {
  return normalize_bank_statement_status(status) === 'processing' ? 3500 : 2500;
}

function normalize_bank_statement_status(value = '') {
  return String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
}

function is_terminal_bank_statement_status(status = '') {
  return ['completed', 'failed', 'rejected'].includes(normalize_bank_statement_status(status));
}

function is_running_bank_statement_status(status = '') {
  return ['queued', 'processing'].includes(normalize_bank_statement_status(status));
}

function get_bank_statement_verification(item = {}) {
  return item?.bank_statement_verification || item?.manual_run?.bank_statement_verification || null;
}

function get_bank_statement_status(item = {}) {
  const verification = get_bank_statement_verification(item);
  return normalize_bank_statement_status(
    verification?.current_status
    || verification?.status
    || verification?.current_attempt?.status
    || ''
  );
}

function get_bank_statement_result(item = {}) {
  const verification = get_bank_statement_verification(item);
  if (!verification) return null;
  return (
    verification?.current_attempt?.result
    || verification?.latest_result
    || verification?.result
    || verification?.analysis_result
    || null
  );
}

function get_bank_statement_raw_result(result = {}) {
  return result?.raw_result_payload || {};
}

function get_bank_statement_result_data(result = {}) {
  const raw_result = get_bank_statement_raw_result(result);
  return raw_result?.data && typeof raw_result.data === 'object'
    ? raw_result.data
    : {};
}

function get_bank_statement_result_analysis(result = {}) {
  const data = get_bank_statement_result_data(result);
  const analysis = data?.analysis;
  return analysis && typeof analysis === 'object' ? analysis : {};
}

function first_nested_non_empty_value(source = {}, paths = []) {
  for (const path of (Array.isArray(paths) ? paths : [])) {
    const value = String(path || '')
      .split('.')
      .filter(Boolean)
      .reduce((current, key) => (current && typeof current === 'object' ? current[key] : undefined), source);
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
}

function find_first_key_value(source = null, keys = []) {
  const targetKeys = new Set(
    (Array.isArray(keys) ? keys : [])
      .map((key) => String(key || '').trim().toLowerCase())
      .filter(Boolean)
  );
  if (!targetKeys.size) return '';

  const queue = [source];
  while (queue.length) {
    const current = queue.shift();
    if (!current || typeof current !== 'object') continue;

    if (Array.isArray(current)) {
      current.forEach((item) => queue.push(item));
      continue;
    }

    for (const [key, value] of Object.entries(current)) {
      if (
        targetKeys.has(String(key || '').trim().toLowerCase())
        && value !== null
        && value !== undefined
        && String(value).trim() !== ''
      ) {
        return value;
      }
      if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }

  return '';
}

function get_bank_statement_summary(result = {}) {
  const analysis = get_bank_statement_result_analysis(result);
  return (
    analysis?.summary
    || result?.summary_json
    || result?.summary
    || {}
  );
}

function get_bank_statement_quality(result = {}) {
  const analysis = get_bank_statement_result_analysis(result);
  return (
    analysis?.quality
    || result?.quality_json
    || result?.quality
    || {}
  );
}

function get_bank_statement_validation(result = {}) {
  const analysis = get_bank_statement_result_analysis(result);
  return (
    analysis?.validation
    || result?.validation_json
    || result?.validation
    || {}
  );
}

function first_non_empty_value(...values) {
  return values.find((value) => (
    value !== null
    && value !== undefined
    && String(value).trim() !== ''
  )) || '';
}

function format_bank_statement_label(value = '') {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

function format_bank_statement_date(value = '') {
  const text = String(value || '').trim();
  if (!text) return '';
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(parsed);
}

function format_bank_statement_date_range(start = '', end = '') {
  const start_label = format_bank_statement_date(start);
  const end_label = format_bank_statement_date(end);
  if (!start_label && !end_label) return '';
  if (!start_label) return end_label;
  if (!end_label) return start_label;
  return `${start_label} - ${end_label}`;
}

function get_bank_statement_period_range(result = {}) {
  const rawResult = get_bank_statement_raw_result(result);
  const data = get_bank_statement_result_data(result);
  const summary = get_bank_statement_summary(result);
  const start = first_non_empty_value(
    result?.period_start,
    data?.period_start,
    summary?.period_start,
    summary?.start_date,
    summary?.first_transaction_date,
    data?.start_date,
    data?.first_transaction_date,
    first_nested_non_empty_value(rawResult, [
      'data.period_start',
      'data.start_date',
      'data.first_transaction_date',
      'data.statement_period.start_date',
      'data.period.start_date',
      'data.analysis.period_start',
      'data.analysis.start_date',
      'data.analysis.first_transaction_date',
      'data.analysis.statement_period.start_date',
      'data.analysis.period.start_date',
      'data.analysis.summary.period_start',
      'data.analysis.summary.start_date',
      'data.analysis.summary.first_transaction_date'
    ]),
    find_first_key_value(rawResult, [
      'period_start',
      'start_date',
      'first_transaction_date',
      'statement_start_date',
      'from_date'
    ])
  );
  const end = first_non_empty_value(
    result?.period_end,
    data?.period_end,
    summary?.period_end,
    summary?.end_date,
    summary?.last_transaction_date,
    data?.end_date,
    data?.last_transaction_date,
    first_nested_non_empty_value(rawResult, [
      'data.period_end',
      'data.end_date',
      'data.last_transaction_date',
      'data.statement_period.end_date',
      'data.period.end_date',
      'data.analysis.period_end',
      'data.analysis.end_date',
      'data.analysis.last_transaction_date',
      'data.analysis.statement_period.end_date',
      'data.analysis.period.end_date',
      'data.analysis.summary.period_end',
      'data.analysis.summary.end_date',
      'data.analysis.summary.last_transaction_date'
    ]),
    find_first_key_value(rawResult, [
      'period_end',
      'end_date',
      'last_transaction_date',
      'statement_end_date',
      'to_date'
    ])
  );
  return {
    start,
    end,
    label: format_bank_statement_date_range(start, end)
  };
}

function parse_bank_statement_number(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const normalized = String(value || '').replace(/[^0-9.-]/g, '');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function format_bank_statement_currency(value, currency = 'ZAR') {
  const amount = parse_bank_statement_number(value);
  if (amount === null) return String(value || '').trim();
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency || 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

function format_bank_statement_percentage(value) {
  const amount = parse_bank_statement_number(value);
  if (amount === null) return String(value || '').trim();
  const normalized = amount <= 1 ? amount * 100 : amount;
  return `${normalized.toFixed(1)}%`;
}

function format_bank_statement_boolean(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return String(value || '').trim();
}

function format_bank_statement_generic_value(value, options = {}) {
  const { type = '', currency = 'ZAR' } = options;
  if (value === null || value === undefined || String(value).trim() === '') return '';
  if (type === 'date') return format_bank_statement_date(value);
  if (type === 'date_range') return format_bank_statement_date_range(value?.start, value?.end);
  if (type === 'currency') return format_bank_statement_currency(value, currency);
  if (type === 'percent') return format_bank_statement_percentage(value);
  if (type === 'boolean') return format_bank_statement_boolean(value);
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
  }
  return String(value).trim();
}

function build_bank_statement_row(id, label, value, options = {}) {
  const formatted_value = format_bank_statement_generic_value(value, options);
  if (!formatted_value) return null;
  return {
    id,
    label,
    value: formatted_value,
    tone: options?.tone || 'default'
  };
}

function is_bank_statement_manual_item(item = {}) {
  const verification_type = String(
    item?.verification_type
    || item?.manual_binding?.verification_type
    || item?.manual_run?.verification_type
    || ''
  ).trim().toLowerCase();
  return verification_type === 'bank_statement';
}

function includes_bank_statement_text(value = '') {
  const normalized = String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
  return normalized.includes('bank statement');
}

function get_answer_identifier(answer = {}) {
  return Number(answer?.id || answer?.answer_id || answer?.application_form_answer_id || 0) || null;
}

function get_answer_file_url(answer = {}) {
  return String(answer?.file_url || answer?.presigned_url || answer?.url || '').trim();
}

function get_answer_label(answer = {}) {
  return String(answer?.question_label || answer?.label || answer?.question || '').trim();
}

function get_manual_mappings(item = {}) {
  return Array.isArray(item?.manual_mappings) ? item.manual_mappings : [];
}

async function fetch_review_mappings_for_run(run = {}, item = {}) {
  const manual_form_link_id = get_run_manual_form_link_id(run) || get_item_manual_form_link_id(item);
  if (!manual_form_link_id) return get_manual_mappings(item);

  const manual_mappings = await fetch_manual_verification_question_mappings(manual_form_link_id)
    .catch(() => []);
  return manual_mappings.length ? manual_mappings : get_manual_mappings(item);
}

async function resolve_manual_review_run(item = {}) {
  const existing_run_id = get_run_id(item);
  if (existing_run_id && !manual_run_is_terminal(item)) {
    return fetch_manual_verification_run(existing_run_id);
  }

  const context = get_manual_review_context(item);
  if (context_missing(context)) {
    throw new Error('This manual verification is missing the context needed to open a review.');
  }

  const response = await open_manual_verification_run(build_open_manual_run_payload(context));
  const run = response?.data || response?.run || response || null;
  if (!run?.id) {
    throw new Error('Manual verification run could not be opened.');
  }

  return run;
}

function get_modal_run_id(manual_review_modal = {}) {
  return Number(manual_review_modal?.run?.id || 0);
}

function get_section_question_id(field_form = {}) {
  return Number(field_form?.section_question_id || 0);
}

export const manageVerificationManualRuntimeMethods = {
  async refresh_bank_statement_workspace_case() {
    if (this.is_detail_page && this.selected_case) {
      await this.hydrate_verification_detail_checks(this.selected_case);
      this.initialize_manual_workspace();
      this.sync_detail_section_state();
      return;
    }

    await this.load_checks();
  },
  async open_manual_workspace_review(item, event) {
    if (is_bank_statement_manual_item(item)) {
      await this.run_bank_statement_workspace_action(item, event);
      return;
    }

    if (manual_run_is_terminal(item)) {
      await this.start_manual_review(item, event);
      return;
    }

    await this.open_manual_review_modal(item, event);
  },
  is_bank_statement_manual_workspace(item = null) {
    return is_bank_statement_manual_item(item || this.manual_workspace_item || {});
  },
  get_bank_statement_analysis(item = null) {
    return get_bank_statement_verification(item || this.manual_workspace_item || {});
  },
  bank_statement_status_label(item = null) {
    const status = get_bank_statement_status(item || this.manual_workspace_item || {});
    if (!status) return 'Not run';
    return status.replace(/\b\w/g, (char) => char.toUpperCase());
  },
  bank_statement_status_tone(item = null) {
    const status = get_bank_statement_status(item || this.manual_workspace_item || {});
    if (status === 'completed') return 'success';
    if (['failed', 'rejected'].includes(status)) return 'danger';
    if (['queued', 'processing'].includes(status)) return 'warning';
    return 'secondary';
  },
  bank_statement_status_message(item = null) {
    const analysis = get_bank_statement_verification(item || this.manual_workspace_item || {});
    const resolved_status = get_bank_statement_status(item || this.manual_workspace_item || {});
    const current_attempt = analysis?.current_attempt || {};
    if (resolved_status === 'queued') return 'The bank statement analysis request has been accepted and is waiting to be processed.';
    if (resolved_status === 'processing') return 'The bank statement is being analysed in the background.';
    if (resolved_status === 'completed') return 'The bank statement analysis completed successfully and the result is ready for review.';
    if (resolved_status === 'failed') return current_attempt?.error_message || analysis?.last_error_message || 'The bank statement analysis failed. Review the error and try again.';
    if (resolved_status === 'rejected') return current_attempt?.error_message || analysis?.last_error_message || 'Encrypted or unsupported bank statements are not allowed.';
    return 'The bank statement analysis has not been started yet.';
  },
  bank_statement_is_running(item = null) {
    return is_running_bank_statement_status(
      get_bank_statement_status(item || this.manual_workspace_item || {})
    );
  },
  bank_statement_progress_value(item = null) {
    const status = get_bank_statement_status(item || this.manual_workspace_item || {});
    if (status === 'queued') return 30;
    if (status === 'processing') return 72;
    if (status === 'completed') return 100;
    if (['failed', 'rejected'].includes(status)) return 100;
    return 0;
  },
  bank_statement_progress_steps(item = null) {
    const status = get_bank_statement_status(item || this.manual_workspace_item || {});
    const is_failed = ['failed', 'rejected'].includes(status);
    return [
      {
        id: 'requested',
        label: 'Verification requested',
        state: status ? 'completed' : 'pending'
      },
      {
        id: 'queued',
        label: 'Waiting for analysis',
        state: status === 'queued'
          ? 'current'
          : (['processing', 'completed', 'failed', 'rejected'].includes(status) ? 'completed' : 'pending')
      },
      {
        id: 'processing',
        label: 'Analyse statement',
        state: status === 'processing'
          ? 'current'
          : (['completed', 'failed', 'rejected'].includes(status) ? 'completed' : 'pending')
      },
      {
        id: 'result',
        label: is_failed ? 'Analysis finished with issues' : 'Generate result',
        state: ['completed', 'failed', 'rejected'].includes(status)
          ? (is_failed ? 'failed' : 'completed')
          : 'pending'
      }
    ];
  },
  bank_statement_primary_action_label(item = null) {
    const target_item = item || this.manual_workspace_item || {};
    const status = get_bank_statement_status(target_item);
    if (is_running_bank_statement_status(status)) return 'Refresh analysis';
    if (status === 'completed' && !manual_run_is_terminal(target_item)) return 'Open review';
    if (status === 'completed') return 'Review again';
    if (['failed', 'rejected'].includes(status)) return 'Run analysis again';
    return Number(target_item?.run_id || 0) > 0 ? 'Run bank statement analysis' : 'Start bank statement analysis';
  },
  bank_statement_header_badges(item = null) {
    const result = get_bank_statement_result(item || this.manual_workspace_item || {});
    if (!result || typeof result !== 'object') return [];

    const rawResult = get_bank_statement_raw_result(result);
    const data = get_bank_statement_result_data(result);
    const summary = get_bank_statement_summary(result);
    const period = get_bank_statement_period_range(result);
    const bank_name = first_non_empty_value(
      (() => {
        const rawBank = first_nested_non_empty_value(rawResult, [
          'data.bank_name',
          'data.bank',
          'data.bank_id',
          'data.analysis.bank_name',
          'data.analysis.bank',
          'data.analysis.bank_id',
          'data.analysis.summary.bank_name',
          'data.analysis.summary.bank',
          'data.analysis.summary.bank_id'
        ]);
        return rawBank ? format_bank_statement_label(rawBank) : '';
      })(),
      (() => {
        const discoveredBank = find_first_key_value(rawResult, [
          'bank_name',
          'bank',
          'bank_id',
          'institution_name',
          'institution',
          'bankid'
        ]);
        return discoveredBank ? format_bank_statement_label(discoveredBank) : '';
      })(),
      data?.bank_name,
      data?.bank,
      data?.bank_id ? format_bank_statement_label(data.bank_id) : '',
      summary?.bank_name,
      summary?.bank
    );
    const layout_name = first_non_empty_value(
      data?.statement_layout,
      data?.layout_name,
      data?.layout_type,
      summary?.statement_layout
    );
    const layout_confidence = first_non_empty_value(
      data?.layout_confidence,
      data?.statement_layout_confidence,
      summary?.layout_confidence
    );
    const extraction_method = first_non_empty_value(
      data?.extraction_method,
      data?.parser_method,
      data?.extraction_mode,
      summary?.extraction_method
    );

    return [
      build_bank_statement_row('bank', 'Bank', bank_name),
      build_bank_statement_row('period', 'Statement period', period.label),
      build_bank_statement_row('layout', 'Statement layout', layout_name),
      build_bank_statement_row(
        'layout-confidence',
        'Layout confidence',
        layout_confidence,
        { type: 'percent' }
      ),
      build_bank_statement_row('extraction-method', 'Extraction method', extraction_method)
    ].filter(Boolean);
  },
  bank_statement_metric_cards(item = null) {
    const result = get_bank_statement_result(item || this.manual_workspace_item || {});
    if (!result || typeof result !== 'object') return [];

    const rawResult = get_bank_statement_raw_result(result);
    const data = get_bank_statement_result_data(result);
    const summary = get_bank_statement_summary(result);
    const quality = get_bank_statement_quality(result);
    const validation = get_bank_statement_validation(result);
    const currency = first_non_empty_value(
      first_nested_non_empty_value(rawResult, [
        'data.currency',
        'data.analysis.currency',
        'data.analysis.summary.currency'
      ]),
      data?.currency,
      summary?.currency,
      'ZAR'
    ) || 'ZAR';
    const period = get_bank_statement_period_range(result);

    return [
      build_bank_statement_row('period', 'Statement period', period.label),
      build_bank_statement_row(
        'verified-income',
        'Verified monthly income',
        first_non_empty_value(
          summary?.verified_monthly_income,
          summary?.stable_recurring_income,
          summary?.average_observed_monthly_credits
        ),
        { type: 'currency', currency, tone: 'success' }
      ),
      build_bank_statement_row(
        'disposable-income',
        'Estimated disposable income',
        first_non_empty_value(
          summary?.estimated_disposable_income,
          summary?.contractual_disposable_income,
          summary?.essential_disposable_income
        ),
        { type: 'currency', currency, tone: 'danger' }
      ),
      build_bank_statement_row(
        'quality-grade',
        'Quality grade',
        first_non_empty_value(result?.quality_grade, quality?.grade),
        { tone: 'warning' }
      ),
      build_bank_statement_row(
        'validation-status',
        'Validation status',
        first_non_empty_value(result?.validation_status, validation?.status, quality?.validation_status),
        { tone: 'warning' }
      ),
      build_bank_statement_row(
        'decision-ready',
        'Decision ready',
        first_non_empty_value(result?.decision_ready, quality?.decision_ready),
        { type: 'boolean', tone: 'danger' }
      ),
      build_bank_statement_row(
        'analysis-months',
        'Analysis months',
        first_non_empty_value(
          summary?.analysis_months,
          summary?.analysis_equivalent_months,
          data?.analysis_months
        )
      ),
      build_bank_statement_row(
        'affordability-surplus',
        'Affordability surplus (avg)',
        first_non_empty_value(
          summary?.average_observed_monthly_net_cashflow,
          summary?.median_observed_monthly_net_cashflow,
          summary?.affordability_surplus_average
        ),
        { type: 'currency', currency, tone: 'danger' }
      )
    ].filter(Boolean);
  },
  bank_statement_result_rows(item = null) {
    return this.bank_statement_metric_cards(item);
  },
  bank_statement_result_sections(item = null) {
    const result = get_bank_statement_result(item || this.manual_workspace_item || {});
    if (!result || typeof result !== 'object') return [];

    const rawResult = get_bank_statement_raw_result(result);
    const data = get_bank_statement_result_data(result);
    const summary = get_bank_statement_summary(result);
    const quality = get_bank_statement_quality(result);
    const validation = get_bank_statement_validation(result);
    const currency = first_non_empty_value(
      first_nested_non_empty_value(rawResult, [
        'data.currency',
        'data.analysis.currency',
        'data.analysis.summary.currency'
      ]),
      data?.currency,
      summary?.currency,
      'ZAR'
    ) || 'ZAR';

    const sections = [
      {
        id: 'metadata',
        label: 'Bank & statement metadata',
        rows: [
          build_bank_statement_row('metadata-bank', 'Bank', first_non_empty_value(
            (() => {
              const rawBank = first_nested_non_empty_value(rawResult, [
                'data.bank_name',
                'data.bank',
                'data.bank_id',
                'data.analysis.bank_name',
                'data.analysis.bank',
                'data.analysis.bank_id',
                'data.analysis.summary.bank_name',
                'data.analysis.summary.bank',
                'data.analysis.summary.bank_id'
              ]);
              return rawBank ? format_bank_statement_label(rawBank) : '';
            })(),
            data?.bank_name,
            data?.bank,
            data?.bank_id ? format_bank_statement_label(data.bank_id) : '',
            summary?.bank_name
          )),
          build_bank_statement_row('metadata-bank-id', 'Bank ID', first_non_empty_value(
            data?.bank_id,
            first_nested_non_empty_value(rawResult, [
              'data.bank_id',
              'data.analysis.bank_id',
              'data.analysis.summary.bank_id'
            ])
          )),
          build_bank_statement_row('metadata-layout', 'Statement layout', first_non_empty_value(
            data?.statement_layout,
            data?.layout_name,
            data?.layout_type
          )),
          build_bank_statement_row('metadata-layout-confidence', 'Layout confidence', first_non_empty_value(
            data?.layout_confidence,
            data?.statement_layout_confidence
          ), { type: 'percent' }),
          build_bank_statement_row('metadata-extraction', 'Extraction method', first_non_empty_value(
            data?.extraction_method,
            data?.parser_method,
            data?.extraction_mode
          )),
          build_bank_statement_row('metadata-currency', 'Currency', first_non_empty_value(
            data?.currency,
            summary?.currency,
            currency
          ))
        ].filter(Boolean)
      },
      {
        id: 'period',
        label: 'Statement period',
        rows: [
          build_bank_statement_row('period-range', 'Period', get_bank_statement_period_range(result).label),
          build_bank_statement_row('period-verified', 'Period verified', first_non_empty_value(
            first_nested_non_empty_value(rawResult, [
              'data.period_is_verified',
              'data.analysis.period_is_verified',
              'data.analysis.summary.period_is_verified'
            ]),
            data?.period_is_verified,
            summary?.period_is_verified
          ), { type: 'boolean' }),
          build_bank_statement_row('period-days', 'Statement days', first_non_empty_value(
            data?.statement_days,
            summary?.statement_days,
            summary?.period_days
          )),
          build_bank_statement_row('period-calendar-months', 'Calendar months covered', first_non_empty_value(
            data?.calendar_months_spanned,
            summary?.calendar_months_spanned
          )),
          build_bank_statement_row('period-complete-months', 'Complete months covered', first_non_empty_value(
            data?.complete_months,
            summary?.complete_months
          ))
        ].filter(Boolean)
      },
      {
        id: 'income',
        label: 'Income detail',
        rows: [
          build_bank_statement_row('income-verified', 'Verified monthly income', first_non_empty_value(
            summary?.verified_monthly_income,
            summary?.stable_recurring_income,
            summary?.average_observed_monthly_credits
          ), { type: 'currency', currency }),
          build_bank_statement_row('income-recurrence-confidence', 'Recurrence confidence', first_non_empty_value(
            summary?.recurrence_confidence,
            summary?.salary_recurrence_confidence
          ), { type: 'percent' }),
          build_bank_statement_row('income-recurring-credits', 'Recurring salary credits', first_non_empty_value(
            summary?.recurring_salary_credits_count,
            summary?.salary_credit_count,
            summary?.recurring_income_events_count
          )),
          build_bank_statement_row('income-payer-pattern', 'Payer pattern', first_non_empty_value(
            summary?.salary_payer_pattern,
            summary?.salary_reference_pattern,
            summary?.recurring_income_reference
          )),
          build_bank_statement_row('income-variable', 'Variable / once-off income', first_non_empty_value(
            summary?.variable_income_amount,
            summary?.once_off_income_amount
          ), { type: 'currency', currency })
        ].filter(Boolean)
      },
      {
        id: 'balances',
        label: 'Balance detail',
        rows: [
          build_bank_statement_row('balance-opening', 'Opening balance', first_non_empty_value(
            summary?.opening_balance,
            data?.opening_balance
          ), { type: 'currency', currency }),
          build_bank_statement_row('balance-closing', 'Closing balance', first_non_empty_value(
            summary?.closing_balance,
            data?.closing_balance
          ), { type: 'currency', currency }),
          build_bank_statement_row('balance-reconciled', 'Balance reconciled', first_non_empty_value(
            summary?.balance_reconciled,
            data?.balance_reconciled
          ), { type: 'boolean' }),
          build_bank_statement_row('balance-discrepancy', 'Balance discrepancy', first_non_empty_value(
            summary?.balance_discrepancy,
            data?.balance_discrepancy
          ), { type: 'currency', currency }),
          build_bank_statement_row('balance-comparable-pairs', 'Comparable transaction pairs', first_non_empty_value(
            summary?.comparable_balance_pairs,
            data?.comparable_balance_pairs
          )),
          build_bank_statement_row('balance-net-cashflow', 'Average monthly net cashflow', first_non_empty_value(
            summary?.average_observed_monthly_net_cashflow,
            summary?.median_observed_monthly_net_cashflow
          ), { type: 'currency', currency })
        ].filter(Boolean)
      },
      {
        id: 'reliability',
        label: 'Overall reliability',
        rows: [
          build_bank_statement_row('reliability-grade', 'Quality grade', first_non_empty_value(
            result?.quality_grade,
            quality?.grade
          )),
          build_bank_statement_row('reliability-score', 'Quality score', first_non_empty_value(
            quality?.score,
            summary?.quality_score
          )),
          build_bank_statement_row('reliability-validation', 'Validation status', first_non_empty_value(
            result?.validation_status,
            validation?.status,
            quality?.validation_status
          )),
          build_bank_statement_row('reliability-decision-ready', 'Decision ready', first_non_empty_value(
            result?.decision_ready,
            quality?.decision_ready
          ), { type: 'boolean' }),
          build_bank_statement_row('reliability-low-confidence', 'Low-confidence transactions', first_non_empty_value(
            quality?.low_confidence_transaction_count,
            summary?.low_confidence_transaction_count
          )),
          build_bank_statement_row('reliability-excluded-credits', 'Unconfirmed credits excluded', first_non_empty_value(
            quality?.excluded_income_credit_count,
            summary?.excluded_income_credit_count
          ))
        ].filter(Boolean)
      }
    ];

    return sections.filter((section) => section.rows.length);
  },
  bank_statement_warning_rows(item = null) {
    const result = get_bank_statement_result(item || this.manual_workspace_item || {});
    if (!result || typeof result !== 'object') return [];

    const warnings = Array.isArray(result?.warnings_json)
      ? result.warnings_json
      : (Array.isArray(result?.warnings) ? result.warnings : []);
    const quality = get_bank_statement_quality(result);
    const data = get_bank_statement_result_data(result);
    const summary = get_bank_statement_summary(result);
    const notes = Array.isArray(quality?.notes) ? quality.notes : [];
    const derived_notes = [
      first_non_empty_value(data?.period_is_verified, summary?.period_is_verified) === false
        ? 'Statement period was inferred from transaction dates and was not confirmed by the bank.'
        : '',
      first_non_empty_value(result?.decision_ready, quality?.decision_ready) === false
        ? 'The analysis is not decision-ready and should be reviewed carefully before relying on the extracted figures.'
        : ''
    ].filter(Boolean);

    return [...warnings, ...notes, ...derived_notes]
      .map((warning, index) => ({
        id: `warning-${index}`,
        value: String(warning || '').trim()
      }))
      .filter((warning) => warning.value);
  },
  clear_bank_statement_poll_timer() {
    if (this.bank_statement_poll_timer_id) {
      window.clearTimeout(this.bank_statement_poll_timer_id);
    }
    this.bank_statement_poll_timer_id = null;
    this.bank_statement_poll_run_id = null;
  },
  schedule_bank_statement_poll(item = null) {
    const target_item = item || this.manual_workspace_item || {};
    const run_id = get_run_id(target_item);
    const status = get_bank_statement_status(target_item);

    if (!run_id || !is_running_bank_statement_status(status)) {
      this.clear_bank_statement_poll_timer();
      return;
    }

    if (this.bank_statement_poll_run_id === run_id && this.bank_statement_poll_timer_id) {
      return;
    }

    this.bank_statement_poll_run_id = run_id;
    this.bank_statement_poll_timer_id = window.setTimeout(async () => {
      this.bank_statement_poll_timer_id = null;

      try {
        await this.refresh_bank_statement_state(target_item);
      } catch (error) {
        this.set_run_feedback(get_error_message(error, 'Unable to refresh the bank statement analysis status.'), 'danger');
        this.clear_bank_statement_poll_timer();
        return;
      }

      const next_item = this.manual_workspace_item || target_item;
      if (is_running_bank_statement_status(get_bank_statement_status(next_item))) {
        this.schedule_bank_statement_poll(next_item);
        return;
      }

      this.clear_bank_statement_poll_timer();
    }, get_bank_statement_poll_delay(status));
  },
  sync_bank_statement_polling(item = null) {
    const target_item = item || this.manual_workspace_item || {};
    if (!this.is_bank_statement_manual_workspace(target_item)) {
      this.clear_bank_statement_poll_timer();
      return;
    }

    if (this.bank_statement_is_running(target_item)) {
      this.schedule_bank_statement_poll(target_item);
      return;
    }

    this.clear_bank_statement_poll_timer();
  },
  resolve_bank_statement_answer_id(item = null) {
    const target_item = item || this.manual_workspace_item || {};
    const case_forms = this.get_case_forms_for_check(target_item);

    for (const form of case_forms) {
      const saved_answers = this.get_form_saved_answers(form);
      const matched_answer = saved_answers.find((answer) => (
        includes_bank_statement_text(get_answer_label(answer))
        && Boolean(get_answer_file_url(answer))
        && get_answer_identifier(answer)
      ));
      if (matched_answer) {
        return get_answer_identifier(matched_answer);
      }
    }

    const documents = Array.isArray(this.selected_case?.documents) ? this.selected_case.documents : [];
    const matched_document = documents.find((document) => (
      includes_bank_statement_text(document?.question || document?.file_name || document?.name || document?.section)
      && Boolean(document?.presigned_url || document?.file_url || document?.url)
      && Number(document?.id || 0) > 0
    ));
    return Number(matched_document?.id || 0) || null;
  },
  async ensure_bank_statement_manual_run(item = {}) {
    let target_item = item;
    if (get_run_id(target_item) && !manual_run_is_terminal(target_item)) {
      return this.manual_workspace_item || target_item;
    }

    const context = get_manual_review_context(target_item);
    if (context_missing(context)) {
      throw new Error('This manual verification is missing the context needed to open a review.');
    }

    await open_manual_verification_run(build_open_manual_run_payload(context));
    await this.refresh_bank_statement_workspace_case();
    return this.manual_workspace_item || target_item;
  },
  async refresh_bank_statement_state(item = null) {
    const target_item = item || this.manual_workspace_item || {};
    const run_id = get_run_id(target_item);
    if (!run_id) return null;

    const response = await refresh_bank_statement_analysis({ run_id });
    await this.refresh_bank_statement_workspace_case();
    return response;
  },
  async poll_bank_statement_state(item = null, max_attempts = 8) {
    let current_item = item || this.manual_workspace_item || {};

    for (let attempt = 0; attempt < max_attempts; attempt += 1) {
      const run_id = get_run_id(current_item);
      if (!run_id) return null;

      await this.refresh_bank_statement_state(current_item);
      current_item = this.manual_workspace_item || current_item;
      const status = get_bank_statement_status(current_item);
      if (is_terminal_bank_statement_status(status)) {
        return fetch_bank_statement_analysis(run_id).catch(() => null);
      }
      if (!is_running_bank_statement_status(status)) {
        return null;
      }
      await sleep(2000);
    }

    this.set_run_feedback('Bank statement analysis is still processing in the background. Refresh again shortly if needed.', 'warning');
    this.sync_bank_statement_polling(current_item);
    return null;
  },
  async run_bank_statement_workspace_action(item, event) {
    event?.stopPropagation?.();
    if (this.is_entity_verification_read_only) {
      this.set_run_feedback('Verification is view-only for entity users.', 'warning');
      return;
    }

    this.set_run_feedback('');
    this.set_manual_check_running(item, true);

    try {
      let target_item = await this.ensure_bank_statement_manual_run(item);
      const current_status = get_bank_statement_status(target_item);

      if (current_status === 'completed') {
        await this.open_manual_review_modal(target_item, event);
        return;
      }

      if (is_running_bank_statement_status(current_status)) {
        this.set_run_feedback('Bank statement analysis is already running in the background.', 'warning');
        this.sync_bank_statement_polling(target_item);
        return;
      }

      const application_form_answer_id = this.resolve_bank_statement_answer_id(target_item);
      if (!application_form_answer_id) {
        this.set_run_feedback('No uploaded bank statement was found for this applicant. Ask the applicant to upload a bank statement before running verification.', 'warning');
        return;
      }

      const run_id = get_run_id(target_item);
      if (!run_id) {
        this.set_run_feedback('A manual verification run could not be opened for this case.', 'danger');
        return;
      }

      const response = await start_bank_statement_analysis({
        run_id,
        application_form_answer_id
      });
      const response_status = normalize_bank_statement_status(response?.status);
      const response_message = response_status === 'rejected'
        ? 'Encrypted or PIN-protected bank statements are not allowed. Ask the applicant to upload an unlocked document.'
        : 'Bank statement analysis started successfully.';

      this.set_run_feedback(response_message, response_status === 'rejected' ? 'danger' : 'success');
      await this.refresh_bank_statement_workspace_case();
      target_item = this.manual_workspace_item || target_item;

      if (is_running_bank_statement_status(get_bank_statement_status(target_item))) {
        this.sync_bank_statement_polling(target_item);
      }
    } catch (error) {
      this.set_run_feedback(get_error_message(error, 'Unable to run the bank statement analysis.'), 'danger');
    } finally {
      this.set_manual_check_running(item, false);
    }
  },
  async start_manual_review(item, event) {
    event?.stopPropagation?.();
    if (this.is_entity_verification_read_only) {
      this.set_run_feedback('Verification is view-only for entity users.', 'warning');
      return;
    }

    if (!this.can_start_manual_review(item)) return;

    const context = get_manual_review_context(item);
    if (context_missing(context)) {
      this.set_run_feedback('This manual verification is missing the context needed to open a review.', 'warning');
      return;
    }

    this.set_run_feedback('');
    this.set_manual_check_running(item, true);

    try {
      const response = await open_manual_verification_run(build_open_manual_run_payload(context));
      this.set_run_feedback(response?.message || `${item?.check || 'Manual verification'} review opened.`, 'success');
      await this.load_checks();
    } catch (error) {
      this.set_run_feedback(get_error_message(error, `Unable to open ${item?.check || 'manual verification'} review.`), 'danger');
    } finally {
      this.set_manual_check_running(item, false);
    }
  },
  async open_manual_review_modal(item, event) {
    event?.stopPropagation?.();

    this.set_run_feedback('');
    this.set_manual_check_running(item, true);

    try {
      const run = await resolve_manual_review_run(item);
      const manual_mappings = await fetch_review_mappings_for_run(run, item);
      const item_with_mappings = { ...item, manual_mappings };
      const configured_sections = this.build_manual_review_sections_from_mappings(item_with_mappings);
      const form_sections = configured_sections.length ? configured_sections : [];
      this.manual_review_modal = build_manual_review_modal_state(item_with_mappings, run, form_sections, this.get_manual_field_runs);
    } catch (error) {
      this.set_run_feedback(get_error_message(error, 'Unable to load the manual verification review.'), 'danger');
    } finally {
      this.set_manual_check_running(item, false);
    }
  },
  close_manual_review_modal() {
    if (this.manual_review_modal.is_submitting) return;

    this.manual_review_modal = build_empty_manual_review_modal();
  },
  async submit_manual_field_result() {
    if (this.is_entity_verification_read_only) {
      this.set_run_feedback('Verification is view-only for entity users.', 'warning');
      return;
    }

    const run_id = get_modal_run_id(this.manual_review_modal);
    const field_form = this.manual_review_modal?.field_form || {};
    if (!run_id || !get_section_question_id(field_form)) return;

    this.manual_review_modal = { ...this.manual_review_modal, is_saving_field: true };

    try {
      const response = await record_manual_verification_field_result(build_field_result_payload(run_id, field_form));
      const next_run = await fetch_manual_verification_run(run_id)
        .catch(() => response?.run || this.manual_review_modal.run);
      const next_item = build_modal_item(this.manual_review_modal.item, next_run, this.get_manual_field_runs);
      this.manual_review_modal = {
        ...this.manual_review_modal,
        is_saving_field: false,
        item: next_item,
        run: next_run,
        field_form: null
      };
      this.set_run_feedback(response?.message || 'Manual field review recorded.', 'success');
      await this.load_checks();
    } catch (error) {
      this.manual_review_modal = { ...this.manual_review_modal, is_saving_field: false };
      this.set_run_feedback(get_error_message(error, 'Unable to save the reviewed field.'), 'danger');
    }
  },
  async submit_manual_review_decision() {
    if (this.is_entity_verification_read_only) {
      this.set_run_feedback('Verification is view-only for entity users.', 'warning');
      return;
    }

    const run_id = get_modal_run_id(this.manual_review_modal);
    if (!run_id) return;

    this.manual_review_modal = { ...this.manual_review_modal, is_submitting: true };

    try {
      const response = await record_manual_verification_decision({
        run_id,
        status: this.manual_review_modal.decision_status,
        decision_reason: this.manual_review_modal.decision_reason || ''
      });
      this.close_manual_review_modal();
      this.set_run_feedback(response?.message || 'Manual verification decision recorded.', 'success');
      await this.load_checks();
    } catch (error) {
      this.manual_review_modal = { ...this.manual_review_modal, is_submitting: false };
      this.set_run_feedback(get_error_message(error, 'Unable to record the manual verification decision.'), 'danger');
    }
  }
};
